import {
  authorColor, getAuthorImageUrl, getAuthorName, mergeAuthor,
  mergePublicAndPrivateItems, populateItemsResponse, scoreCompleteness,
} from '../../common/ItemTools';
import { buildMediaTypeNameMap, MediaTypes } from '../../common/MediaTypes';
import { buildCodeLanguageMap } from '../../common/LanguageCodes';
import { dateTimeToInput, formatDate, timestampToInput } from '../common/hero/utils';

export const typeNameMap = buildMediaTypeNameMap();
const languageCodeMap = buildCodeLanguageMap();

export function typeColor(typeVal) {
  const t = typeNameMap[typeVal];
  return (t && t.color) || 'gray';
}

export function typeLabel(typeVal) {
  const t = typeNameMap[typeVal];
  return (t && t.label ? t.label : 'unknown').toLowerCase();
}

export function languageName(code) {
  return (code && languageCodeMap[code] && languageCodeMap[code].name) || code || '';
}

// Verb for the primary CTA, chosen by media type so "Read at" doesn't show on
// a video or podcast. Defaults to "Open".
export function ctaVerb(type) {
  switch (type) {
    case MediaTypes.Video:
    case MediaTypes.YouTubeChannel:
      return 'Watch';
    case MediaTypes.Episode:
    case MediaTypes.Podcast:
    case MediaTypes.Music:
      return 'Listen';
    case MediaTypes.Article:
    case MediaTypes.Book:
    case MediaTypes.Paper:
    case MediaTypes.Digest:
    case MediaTypes.Discussion:
      return 'Read';
    case MediaTypes.Diagram:
    case MediaTypes.Presentation:
      return 'View';
    default:
      return 'Open';
  }
}

export function itemUrlEntries(item) {
  const links = item.links || [];
  const primary = links.find((l) => l.primary);
  const rest = links.filter((l) => !l.primary);
  return [...(primary ? [primary] : []), ...rest]
    .filter((l) => l.url)
    .map((l) => ({ url: l.url, visible: l.primary ? true : Boolean(l.visible) }));
}

export function publishedIsDateOnly(item) {
  return item.timePublishedDateOnly ?? !item.timePublished;
}

export function parseReadTime(text) {
  const m = /(\d+)/.exec(text || '');
  return m ? parseInt(m[1], 10) : 0;
}

export function suggestionPatch(field, value) {
  switch (field) {
    case 'type':
      return { type: Number(value) };
    case 'timeNeeded':
      return { readTime: `${value} min` };
    case 'timePublished': {
      const dateOnly = value % 86400 === 0;
      return { datePublished: timestampToInput(value, dateOnly), dateOnly };
    }
    default:
      return { [field]: value };
  }
}

// What the draft held in the keys `patch` is about to overwrite.
export function restoreSlice(draft, patch) {
  return Object.keys(patch).reduce(
    (slice, key) => ({ ...slice, [key]: draft[key] }),
    {},
  );
}

// How a candidate reads in the panel, for the fields whose stored value isn't
// what the editor shows.
export function suggestionDisplay(field, value) {
  switch (field) {
    case 'type':
      return typeLabel(Number(value));
    case 'lang':
      return languageName(value);
    case 'timeNeeded':
      return `${value} min`;
    case 'timePublished':
      return formatDate(value, value % 86400 === 0);
    default:
      return value;
  }
}

/**
 * Whether Look up has a URL left to fetch, judged from Source data's extras — a
 * link with no entry was never fetched. Mirrors enhanceUnfetchedItemUrls on the server.
 */
export function hasUnfetchedUrls(item, extras) {
  if (!extras) return false;
  const links = [...(item.publicData?.links || []), ...(item.privateData?.links || [])];
  return links.some((link) => {
    if (!link.url) return false;
    const entry = extras.find((e) => (link.urlId && e.urlId === link.urlId) || e.url === link.url);
    const extra = entry?.extra;
    return !extra?.web?.stats?.refreshedAt || !extra?.ai?.stats?.refreshedAt;
  });
}

// A Look up that found every page already fetched is a success too, and says so.
export function lookUpResultMessage({ fetched, ai }) {
  if (!fetched) return 'Nothing to look up — every page has been fetched.';
  const pages = fetched === 1 ? '1 page' : `${fetched} pages`;
  return ai ? `Looked up ${pages}, ${ai} with AI.` : `Looked up ${pages}.`;
}

// Completeness of the draft rather than the saved item.
export function draftCompleteness(draft) {
  return scoreCompleteness({
    title: Boolean(draft.title.trim()),
    link: draft.urls.some((u) => u.url.trim()),
    authors: draft.authors.some((a) => a.name.trim()),
    coverImageUrl: Boolean(draft.coverImageUrl.trim()),
    excerpt: Boolean(draft.excerpt.trim()),
    type: Boolean(Number(draft.type)),
    lang: Boolean(draft.lang),
    timeNeeded: parseReadTime(draft.readTime) > 0,
    timePublished: Boolean(draft.datePublished),
    tags: draft.tags.length > 0,
  });
}

// The profile fields the server stores for an author.
const AUTHOR_PROFILE_FIELDS = ['name', 'username', 'url', 'imageUrl'];

// An author suggestion's profile, kept whole.
export function authorCandidateProfile(candidate) {
  const profile = {};
  AUTHOR_PROFILE_FIELDS.forEach((field) => {
    const value = candidate[field];
    if (typeof value === 'string' && value.trim()) profile[field] = value.trim();
  });
  return profile;
}

/**
 * The profile a draft author entry is created from. The name field stays
 * editable, the user's wording wins — except over a byline that gave only a
 * username, where there is no name to invent.
 */
export function authorProfileForCreate(entry) {
  const profile = { ...(entry.profile || {}) };
  const name = entry.name.trim();
  if (profile.name || name !== profile.username) profile.name = name;
  return profile;
}

export function combineFetchedItem(response) {
  const { item } = response || {};
  if (!item) return null;
  populateItemsResponse([item], response);
  return mergePublicAndPrivateItems(item);
}

export function buildDraft(item) {
  const authors = (item.authors || []).map((a) => {
    const name = getAuthorName(a) || '';
    const merged = mergeAuthor(a);
    const imageUrl = merged ? getAuthorImageUrl(merged, merged.authorImageScope) : null;
    return { authorId: a.id, name, color: authorColor(name), imageUrl };
  });
  return {
    title: item.title || '',
    excerpt: item.excerpt || '',
    coverImageUrl: item.coverImageUrl || '',
    type: item.type || 0,
    lang: item.lang || '',
    readTime: item.timeNeeded ? `${item.timeNeeded} min` : '',
    datePublished: timestampToInput(item.timePublished, publishedIsDateOnly(item)),
    dateOnly: publishedIsDateOnly(item),
    dateCreated: dateTimeToInput(item.timeCreated),
    dateReviewed: dateTimeToInput(item.timeReviewed),
    authors,
    urls: itemUrlEntries(item).length ? itemUrlEntries(item) : [{ url: '', visible: true }],
    tags: (item.tags || []).map((t) => ({ id: t.id, name: t.name })),
    related: (item.relatedItems || []).map((r) => ({ id: r.id })),
  };
}
